/** Human-readable labels for backend notification `type` strings. */
export const NOTIFICATION_TYPE_LABELS = {
  // Team / roles
  team_role_created: 'Role created',
  team_role_updated: 'Role updated',
  team_role_deleted: 'Role deleted',
  team_user_created: 'User created',
  team_user_updated: 'User updated',
  team_user_status_changed: 'User status changed',
  team_user_role_changed: 'User role changed',
  team_mentor_created: 'Mentor added',
  team_mentor_updated: 'Mentor updated',
  team_mentor_deleted: 'Mentor removed',
  team_jury_created: 'Jury member added',
  team_jury_updated: 'Jury member updated',
  team_jury_deleted: 'Jury member removed',
  team_founder_created: 'Founder added',
  team_assignment_created: 'Assignment created',
  team_assignment_updated: 'Assignment updated',
  team_assignment_removed: 'Assignment removed',
  team_stakeholder_created: 'Stakeholder added',
  team_member_changed: 'Member updated',
  team_member_removed: 'Member removed',

  // Assignments (panel roles)
  assignment_mentor_created: 'Mentor role assigned',
  assignment_jury_created: 'Jury role assigned',
  assignment_founder_created: 'Founder role assigned',
  assignment_mentor_updated: 'Mentor role updated',
  assignment_jury_updated: 'Jury role updated',
  assignment_founder_updated: 'Founder role updated',
  assignment_mentor_revoked: 'Mentor role revoked',
  assignment_jury_revoked: 'Jury role revoked',
  assignment_founder_revoked: 'Founder role revoked',

  // Mentoring
  mentor_session_requested: 'Session request',
  mentor_session_scheduled: 'Session scheduled',
  mentor_session_confirmed: 'Session confirmed',
  mentor_session_rescheduled: 'Session rescheduled',
  mentor_session_updated: 'Session updated',
  mentor_session_cancelled: 'Session cancelled',
  mentor_session_planning_updated: 'Session planning updated',
  mentor_assignment_created: 'Mentor assignment',
  mentor_assigned: 'Mentor assigned',
  mentor_assignment_active: 'Assignment confirmed',
  mentor_assignment_completed: 'Assignment completed',
  mentor_assignment_escalated: 'Assignment escalated',
  mentor_verification_approved: 'Verification approved',
  mentor_verification_rejected: 'Verification update',

  // Jury
  jury_application_assigned: 'Jury assignment',
  application_jury_assigned: 'Jury evaluation started',
  jury_score_submitted: 'Jury score submitted',
  jury_score_received: 'Jury score received',
  jury_evaluation_reminder: 'Evaluation reminder',

  // Applications / CFA
  application_step: 'Application step',
  application_field_updated: 'New application data',
  application_status_accepted: 'Application accepted',
  application_status_rejected: 'Application rejected',
  application_status_shortlisted: 'Application shortlisted',
  application_status_jury_in_progress: 'Jury in progress',
  application_status_jury_completed: 'Jury completed',
  application_status_funded: 'Application funded',

  // Startup profile
  startup_profile_submitted: 'Profile submitted',
  startup_profile_resubmitted: 'Profile resubmitted',
  startup_profile_approved: 'Profile approved',
  startup_profile_rejected: 'Profile rejected',
  startup_profile_needs_revision: 'Revision requested',

  // To-dos
  todo_assigned: 'To-do assigned',
  todo_mention: 'To-do mention',
  todo_overdue: 'Task overdue',
  todo_completed: 'To-do completed',
  todo_status_changed: 'To-do status changed',
  todo_deleted: 'To-do deleted',
  todo_list_deleted: 'To-do list deleted',

  // Chat
  chat_message: 'New message',
  chat_mention: 'Mention',

  // Documents & funding
  document_verified: 'Document verified',
  document_rejected: 'Document rejected',
  document_uploaded: 'Document uploaded',
  document_deleted: 'Document deleted',
  document_commented: 'Document comment',
  funding_record_created: 'Funding added',
  funding_status_approved: 'Funding approved',
  funding_status_disbursed: 'Funding disbursed',
  funding_acknowledged: 'Funding acknowledged',
  fund_created: 'Fund created',
  fund_updated: 'Fund updated',
  fund_status_changed: 'Fund status changed',
  fund_archived: 'Fund archived',
  fund_usage_created: 'Expense recorded',
  installment_created: 'Disbursement schedule set',
  installment_paid: 'Installment paid',
  installment_acknowledged: 'Installment acknowledged',

  // Events
  event_created: 'Event created',
  event_updated: 'Event updated',
  event_status_changed: 'Event status changed',
  event_rescheduled: 'Event rescheduled',
  event_cancelled: 'Event cancelled',
  event_material_uploaded: 'Event material added',
  event_material_deleted: 'Event material removed',
  event_speaker_assigned: 'Speaker assigned',
  event_speaker_updated: 'Speaker line-up updated',
  registration_registered: 'Event registration',
  registration_cancelled: 'Registration cancelled',
  registration_checked_in: 'Checked in',

  // Milestones
  milestone_created: 'Milestone created',
  milestone_updated: 'Milestone updated',
  milestone_deleted: 'Milestone deleted',
  milestone_assigned: 'Milestone assigned',
  milestone_completed: 'Milestone completed',
  milestone_status_changed: 'Milestone status changed',
  milestone_template_created: 'Milestone template created',
  milestone_template_updated: 'Milestone template updated',
  milestone_template_deleted: 'Milestone template deleted',

  // Evaluations
  evaluation_template_created: 'Evaluation template created',
  evaluation_template_updated: 'Evaluation template updated',
  evaluation_template_duplicated: 'Evaluation template duplicated',
  evaluation_template_deleted: 'Evaluation template deleted',
  evaluation_binding_created: 'Rubric attached',
  evaluation_binding_changed: 'Rubric changed',
  evaluation_binding_removed: 'Rubric detached',

  // Speakers
  speaker_created: 'Speaker added',
  speaker_updated: 'Speaker updated',
  speaker_deleted: 'Speaker removed',

  // Startups & invites
  startup_status_changed: 'Startup status changed',
  startup_deleted: 'Startup deleted',
  invite_created: 'Invite sent',
  invite_submitted: 'Invite submitted',
  invite_approved: 'Invite approved',
  invite_denied: 'Invite denied',
  invite_cancelled: 'Invite cancelled',

  // Platform / account
  organization_created: 'Organization update',
  organization_status_changed: 'Organization status changed',
  organization_subscription_expiring: 'Subscription ending soon',
  organization_renewal_requested: 'Subscription renewal requested',
  center_settings_updated: 'Center settings updated',
  profile_updated: 'Profile updated',
  password_changed: 'Password changed',
};

export function notificationTypeLabel(type) {
  if (!type) return '';
  if (NOTIFICATION_TYPE_LABELS[type]) return NOTIFICATION_TYPE_LABELS[type];
  if (type.startsWith('application_status_')) {
    const status = type.replace('application_status_', '').replace(/_/g, ' ');
    return `Application: ${status}`;
  }
  // center_status_* is generated per status value, so it can't be enumerated.
  if (type.startsWith('center_status_')) {
    const status = type.replace('center_status_', '').replace(/_/g, ' ');
    return `Center: ${status}`;
  }
  if (type.startsWith('team_')) {
    return type.replace('team_', '').replace(/_/g, ' ');
  }
  return type.replace(/_/g, ' ');
}
